// Packages and components
import { useEffect, useState, useCallback } from 'react';

// Helpers and utilities
import { useFuzzySearch } from '@hooks/useFuzzySearch';
import axios from '@helpers/axios';
import { strogging } from '@shd/jslib/infra';
import * as Sentry from '@sentry/browser';
import { shd } from '@shd/jslib/models';
import { DateTime } from 'luxon';

import type {
  CreateTeamResponse,
  ExternalTeamSearchResultInput,
  Team,
  TeamSearchResponse,
} from '@dto/api';

import type {
  TeamImportPartnerName,
  ErrorMessage,
  InviteBody,
  StaffMember,
} from '@theme/theme.d';

import type {
  ImportTeamSearchObject,
  InviteRosterRequest,
  PlayerCreateResponse,
} from '@features/Onboarding/Onboarding.d';

export const useDeleteOrArchiveTeam = (teamId: string | undefined) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [canDelete, setCanDelete] = useState<boolean>(false);
  const [processComplete, setProcessComplete] = useState<boolean>(false);

  useEffect(() => {
    if (!teamId) {
      return;
    }
    const checkIfTeamCanBeDeleted = async () => {
      try {
        const response = await axios.get(`/api/team/${teamId}/can_be_deleted`);
        setCanDelete(response.data);
        setLoading(false);
      } catch (error) {
        strogging.log('Error checking team options', error);
      } finally {
        setLoading(false);
      }
    };

    checkIfTeamCanBeDeleted();
  }, [teamId]);

  const archiveTeam = async () => {
    try {
      axios.put(`/api/team/${teamId}/archive`).then(async (response) => {
        strogging.log(`Archiving account for team with ID: ${teamId}`);
        if (response.status === 200) {
          setProcessComplete(true);
          strogging.log(`Archived account for team with ID: ${teamId}`);
        }
      });
    } catch (error) {
      strogging.log('Error removing team', error);
    }
  };

  const deleteTeam = async () => {
    try {
      axios.delete(`/api/team/${teamId}/delete`).then(async (response) => {
        strogging.log(`Deleting account for team with ID: ${teamId}`);
        if (response.status === 200) {
          setProcessComplete(true);
          strogging.log(`Deleted account for team with ID: ${teamId}`);
        }
      });
    } catch (error) {
      strogging.log('Error removing team', error);
    }
  };

  return {
    archiveTeam,
    canDelete,
    deleteTeam,
    loading,
    processComplete,
  };
};

export const useImportTeam = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<ErrorMessage>();
  const [importComplete, setImportComplete] = useState<boolean>(false);

  const onImportTeam = async (team: ExternalTeamSearchResultInput) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `/v2/3p/${team.source}/teams/import`,
        {
          team: team,
          players: team.players,
          localTzString: DateTime.local().zoneName,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      if (response.status === 200) {
        setImportComplete(true);
        return response.data as CreateTeamResponse;
      }
    } catch (err) {
      strogging.log(`Error importing ${team.source} team`, err);
      setError({ message: `Error importing ${team.source} team` });
    } finally {
      setLoading(false);
    }
  };

  return {
    error,
    importComplete,
    loading,
    onImportTeam,
  };
};

export const useImportTeamPlayers = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const onImportTeamPlayers = async (
    importPartner: TeamImportPartnerName,
    teamId: string | number
  ) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `/v2/3p/${importPartner}/teams/${teamId}/players`
      );
      if (response.status === 200) {
        return response.data;
      }
    } catch (error) {
      strogging.log('Error importing team players', error);
    } finally {
      setLoading(false);
    }
  };

  return {
    onImportTeamPlayers,
    loading,
  };
};

export const useImportTeamSearch = () => {
  const endpoint = '/v2/3p/search';

  const { debouncing, error, loading, onClearResults, results, search } =
    useFuzzySearch<TeamSearchResponse>(endpoint);

  const onSearch = async (searchObject: ImportTeamSearchObject) => {
    await search(searchObject);
  };

  return {
    debouncing,
    error,
    loading,
    onClearResults,
    onSearch,
    results,
  };
};

export const useImportTeamSnap = () => {
  const [isListeningForAuth, setIsListeningForAuth] = useState<boolean>(false);
  const [error, setError] = useState<ErrorMessage>();
  const [accessTokenAdded, setAccessTokenAdded] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const onImportTeamSnap = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/v2/3p/teamsnap/teams`);
      if (response.status === 200) {
        return response.data;
      }
    } catch (err) {
      strogging.log('Error importing team players', err);
    } finally {
      setLoading(false);
    }
  };

  const postAccessToken = async (accessToken: string) => {
    const endpoint = '/v2/3p/teamsnap/access_token';
    axios
      .post(endpoint, { access_token: accessToken })
      .then(() => {
        setAccessTokenAdded(true);
        setTimeout(() => {
          window.close();
        }, 500);
      })
      .catch(() => {
        setError({ message: 'TeamSnap login failed.' });
      });
  };

  return {
    accessTokenAdded,
    error,
    isListeningForAuth,
    loading,
    onImportTeamSnap,
    postAccessToken,
    setIsListeningForAuth,
  };
};

export const useAddTeamPlayer = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const addTeamPlayer = async (
    player: InviteRosterRequest,
    teamId: string
  ): Promise<PlayerCreateResponse | undefined> => {
    try {
      setLoading(true);
      const response = await axios.post<PlayerCreateResponse>(
        `/v2/teams/${teamId}/players`,
        player
      );
      if (response.status === 200) {
        setLoading(false);
        return response.data;
      }
    } catch (error) {
      strogging.log('Error adding team player', error);
    }
  };
  return {
    addTeamPlayer,
    loading,
  };
};

export const useTeamStaff = () => {
  const sendInvite = useCallback(async (body: InviteBody) => {
    return axios
      .post('/api/user_invites', body)
      .then((response) => {
        const { invites: newInvites } = response.data;
        return newInvites.map((invite: shd.Invite) => ({
          nameFirst: invite.invitedUserNameFirst || 'awaiting',
          nameLast: invite.invitedUserNameLast || ' user to accept invite',
          phoneList: [invite.invitedUserPhone],
          roles: [invite.teamRole],
          inviteCode: invite.id,
          pending: true,
          expires: invite.expires,
        }));
      })
      .catch((e) => {
        Sentry.captureException(e);
        if (e.response && e.response.data) {
          throw new Error(e.response.data.message);
        } else {
          throw new Error(e.toString());
        }
      });
  }, []);

  const fetchTeamStaff = useCallback(async (teamId: string) => {
    return axios.get(`/api/team/${teamId}/roles`).then((response) => {
      return response.data.users;
    });
  }, []);

  const updateStaff = useCallback(
    async (staffMemberId: string | undefined, payload: StaffMember) => {
      return axios
        .put(`/api/user/${staffMemberId}`, { newUser: payload })
        .then((resp) => {
          return resp;
        })
        .catch((respError) => {
          Sentry.captureException(respError);
          throw new Error(respError.toString());
        });
    },
    []
  );

  const deleteStaff = useCallback(
    async (staffMemberId: string | undefined, payload: StaffMember) => {
      return axios
        .put(`/api/user/${staffMemberId}`, { newUser: payload })
        .then((response) => response)
        .catch((respError) => {
          Sentry.captureException(respError);
          throw new Error(respError.toString());
        });
    },
    []
  );

  return { sendInvite, fetchTeamStaff, updateStaff, deleteStaff };
};

export const useTeam = (teamId: string) => {
  const [team, setTeam] = useState<Team | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<ErrorMessage>();

  const getTeam = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/v2/teams/${teamId}`);
      setTeam(response.data);
      setLoading(false);
    } catch (err) {
      setError({ message: `Error fetching team: ${err}` });
      strogging.log('Error fetching team', err);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  return {
    team,
    loading,
    error,
    getTeam,
  };
};
